import { t } from "src/translations/help"
import _, { filter, find, intersection, maxBy, orderBy, uniq } from "lodash"
import { getDateByDateString, currentDate } from "src/utils/CommonMethods"
import {
  CHECKED_OFFERINGS,
  DI_TAB_ALLOWED_EQUIP_TYPES,
  DI_TAB_V2_ALLOWED_EQUIP_TYPES,
  DIOfferings,
  DIOfferingsMappedToEquipmentTypes,
  ENABLING_AHUPR_REPORT_OFFERINGS,
  ENABLING_CPR_REPORT_OFFERINGS,
  ENABLING_DI_REPORT_OFFERINGS,
  ENABLING_EHR_REPORT_OFFERINGS,
  ENABLING_SA_REPORT_OFFERINGS,
  OFFERING_STATUS,
  OFFERING_STATUS_TO_DISPLAY
} from "./constants"
const DI_VRF_OFFERINGS = ["DI-VRF", "CAP"]

export const offeringDIAvailableEquipmentTypes = (offerings: any[]) => {
  if (!offerings || offerings?.length === 0) return []

  const offeringsCodes =
    uniq(
      offerings?.filter((obj) => !obj?.pastGracePeriod).map((obj) => obj.code)
    ) || []
  const commonOfferings = intersection(DIOfferings, offeringsCodes)
  const DIOfferingsNames = offerings?.filter(
    (item: any) => item?.code?.startsWith("DI-") && !item?.pastGracePeriod
  )
  const availableEquipmentTypes = []
  DIOfferingsNames?.forEach((offering: any) => {
    if (DIOfferingsMappedToEquipmentTypes[offering?.code]) {
      availableEquipmentTypes?.push(
        ...(DIOfferingsMappedToEquipmentTypes[offering?.code]?.split(","))
      )
    }
  })
  if (commonOfferings.length > 0) {
    return uniq([...availableEquipmentTypes, "Chiller", "AHU","HeatPumpChiller"])
  }
  return [...availableEquipmentTypes]
}

export const decideShowDITab = (
  offerings = [],
  equipmentType = "",
  isV2 = false
) => {
  return (
    offeringDIAvailableEquipmentTypes(offerings)?.includes(equipmentType) ||
    (isV2
      ? DI_TAB_V2_ALLOWED_EQUIP_TYPES?.includes(equipmentType)
      : DI_TAB_ALLOWED_EQUIP_TYPES?.includes(equipmentType))
  )
}

export const decideShowVRFDITab = (offerings = [], equipmentType) => {
  return (
    intersection(offerings, DI_VRF_OFFERINGS).length > 0 &&
    DI_TAB_ALLOWED_EQUIP_TYPES?.includes(equipmentType)
  )
}

export const decideShowVRFDIReport = (offerings = [], equipmentTypes) => {
  return (
    intersection(offerings, DI_VRF_OFFERINGS).length > 0 &&
    intersection(DI_TAB_ALLOWED_EQUIP_TYPES, equipmentTypes).length > 0
  )
}

export const iSDiReportDisabled = (equipmentType, buildingSummary) => {
  return intersection(
    offeringDIAvailableEquipmentTypes(buildingSummary?.processedOfferings),
    equipmentType
  ).length > 0
    ? !buildingSummary?.enableReports?.DI
    : true
}

export const processOfferings: any = (
  rawOfferings = [],
  tz,
  equipmentLevelDetails = {}
) => {
  let processedOfferings = _(rawOfferings)
    .filter((o) => OFFERING_STATUS?.[o?.status])
    .groupBy("code")
    .mapValues((items) => {
      // Step 1: Pick the latest end date record
      const latestEndDate = maxBy(items, (item) => new Date(item?.endDate))
      let offerings = filter(
        items,
        (item) =>
          new Date(item?.endDate).getTime() ===
          new Date(latestEndDate?.endDate).getTime()
      )

      // Step 2: If all have the same end date, pick the record with the bigger grace period
      const maxGracePeriod = maxBy(offerings, "gracePeriod")
      offerings = filter(
        offerings,
        (item) => item.gracePeriod === maxGracePeriod.gracePeriod
      )

      let finalOffering = offerings?.[0]

      // Step 3: If all have the same end date and same grace period, prefer the approved record
      const approvedOffering = find(offerings, { status: "Approved" })
      if (approvedOffering) {
        finalOffering = approvedOffering
      }

      // Step 4: Process the offering
      const processOffering = getExpiredStatus(finalOffering, tz)
      return {
        ...finalOffering,
        ...processOffering
      }
    })
    .value()

  processedOfferings = orderBy(
    Object.values(processedOfferings || {}) || [],
    ["code"],
    ["asc"]
  )

  const offeringCodes = []
  const activeOfferingCodes = []
  processedOfferings?.forEach((o) => {
    if (o?.isActive) {
      activeOfferingCodes.push(o?.code || "")
    }
    offeringCodes.push(o?.code || "")
  })

  return {
    processedOfferings,
    hasActiveOffering: enableBasedOnOfferings(
      processedOfferings,
      CHECKED_OFFERINGS,
      equipmentLevelDetails
    ),
    enableReports: {
      AHUPR: enableBasedOnOfferings(
        processedOfferings,
        ENABLING_AHUPR_REPORT_OFFERINGS,
        equipmentLevelDetails
      ),
      CPR: enableBasedOnOfferings(
        processedOfferings,
        ENABLING_CPR_REPORT_OFFERINGS,
        equipmentLevelDetails
      ),
      EHR: enableBasedOnOfferings(
        processedOfferings,
        ENABLING_EHR_REPORT_OFFERINGS,
        equipmentLevelDetails
      ),
      SA: enableBasedOnOfferings(
        processedOfferings,
        ENABLING_SA_REPORT_OFFERINGS,
        equipmentLevelDetails
      ),
      DI: enableBasedOnOfferings(
        processedOfferings,
        ENABLING_DI_REPORT_OFFERINGS
      )
    },
    offeringCodes,
    activeOfferingCodes
  }
}

export const getExpiredStatus = (offering: any, tz: string) => {
  const { endDate, gracePeriod } = offering
  const expiredDays = currentDate(tz).diff(
    getDateByDateString(endDate, tz),
    "days"
  )
  const remainingDaysNumberInGracePeriod = gracePeriod - expiredDays
  //const isExpired = status === OFFERING_STATUS.Expired;
  const pastGracePeriod = remainingDaysNumberInGracePeriod < 0
  const isExpired = pastGracePeriod
  return {
    isExpired,
    remainingDaysNumberInGracePeriod,
    pastGracePeriod
  }
}

// ATS, Performance, SA, reports based on Checked respective offerings
export const enableBasedOnOfferings = (
  offerings,
  checkedOfferings = [],
  equipmentLevelDetails: any = {}
) => {
  const {
    equipmentLevel = false,
    analyticsListConfig = {},
    equipmentType = ""
  } = equipmentLevelDetails
  if (equipmentLevel) {
    if (!Object?.keys(analyticsListConfig)?.includes(equipmentType)) {
      return false
    }
  }
  if (offerings?.length > 0) {
    return !offerings
      .filter((offering: any) => checkedOfferings?.includes(offering?.code))
      .every((item: any) => item?.pastGracePeriod)
  } else {
    return false
  }
}

export const showGracePeriod = (offering) => {
  return offering?.isExpired
    ? offering?.remainingDaysNumberInGracePeriod >= 0
      ? offering?.remainingDaysNumberInGracePeriod
      : t("common.na")
    : t("common.na")
}
